import { createAction, props } from '@ngrx/store';

import { IWorkspace } from '../../../modules/chat/interfaces/IWorkspace';
import { IAttachment } from '../interfaces/IAttachment';
import {
  ChannelConsent,
  ChannelServiceType,
  ChannelStatus,
  IChannel,
  IChannelByParams,
  IChannelFilter,
  IChannelOwner,
} from '../interfaces/IChannel';
import { IDealer } from '../interfaces/IDealer';
import { DeliveryStatusType, IMessage, IReaction, Suggestion } from '../interfaces/IMessage';
import { IMessageableEntity } from '../interfaces/IMessageableEntity';
import { IMessageTemplate } from '../interfaces/IMessageTemplate';
import { IRepairOrder } from '../interfaces/IRepairOrder';
import { IStandardResponse, IStandardResponseContext } from '../interfaces/IStandardResponse';
import { ITag } from '../interfaces/ITag';
import { IVideoCallSettings } from '../interfaces/IVideoCallMember';
import { ILog } from '../services/logs.service';
import { IChatState } from './chat.reducer';

export const sendMessage = createAction(
  '[App] Send Message',
  props<{
    message: string | null;
    attachments: IAttachment[];
    log?: ILog | null;
    template?: IMessageTemplate | null;
  }>()
);

export const setSmartRepliesStatus = createAction(
  '[App] Set smart replies status',
  props<{ value: boolean; channel: IChannel }>()
);

export const setLastMessage = createAction('[App] Set last message', props<{ message: IMessage; channel: IChannel }>());

export const selectWorkspace = createAction('[App] Select Workspace', props<{ selectedWorkspace: IWorkspace }>());

export const loadWorkspaces = createAction('[App] Load WorkSpace', props<{ workspaces: IWorkspace[] }>());

export const loadChannels = createAction(
  '[App] Get channels',
  props<{
    autoSelectChannel: boolean;
    channelID?: string | null;
    phoneNumber?: string;
    routing?: ChannelServiceType;
    setLoading: boolean;
    concat: boolean;
  }>()
);

export const updateOwnerOnChannelsAndMessagesByID = createAction(
  '[App] update owner on channels and messages by id',
  props<{
    ownerID: string;
    ownerDisplayName: string;
    ownerProfilePic: string;
  }>()
);

export const changeBotMode = createAction(
  '[App] Change bot mode',
  props<{
    value: boolean;
    channel: IChannel;
  }>()
);

export const setBotMode = createAction(
  '[App] Set bot mode',
  props<{
    value: boolean;
    channel: IChannel;
  }>()
);

export const setChannels = createAction('[App] Set channels', props<{ channels: IChannel[]; concat: boolean }>());

export const changeChannelOwner = createAction(
  '[App] change channel owner',
  props<{ channel: IChannel; owner: IChannelOwner }>()
);

export const setAuthorizationTokenKey = createAction('[App] set authorization token key', props<{ key: string }>());

export const setChannelOwner = createAction(
  '[App] set channel owner',
  props<{ channel: IChannel; owner: IChannelOwner; tags: string[] }>()
);

export const selectChannel = createAction('[App] Select channel', props<{ channel: IChannel }>());

export const toggleSidebar = createAction('[App]  Toggle Sidebar', props<{ active: boolean }>());

export const changeOPTOut = createAction('[App]  Change OptOut', props<{ optOut: boolean }>());

export const changeOPTIn = createAction('[App]  Change OptIn', props<{ optIn: boolean }>());

export const changeOPT = createAction('[App]  Change enabled', props<{ enabled: boolean }>());

export const changeChannelEnabledStatus = createAction(
  '[App] Change channel status enabled',
  props<{ channel: IChannel; enabled: boolean }>()
);

export const setChannelEnabledStatus = createAction(
  '[App] set channel status enabled',
  props<{ channel: IChannel; enabled: boolean }>()
);
export const setChannelDisplayName = createAction(
  '[App] set channel display name',
  props<{ channel: IChannel; displayName: string }>()
);

export const deselectChannel = createAction('[App] Deselect channel');

export const getChannel = createAction(
  '[App] Get channel',
  props<{
    channelUID: string | null;
    autoSelect: boolean;
    channelByParams?: IChannelByParams;
    tryCreateChannel?: boolean;
    updateChannel?: boolean;
    ignoreTags?: boolean;
  }>()
);

export const setChannelInfo = createAction(
  '[App] set channel info',
  props<{
    channel: IChannel;
    displayName: string;
  }>()
);

export const setChannelPhone = createAction(
  '[App] set channel Phone',
  props<{
    channel: IChannel;
    phoneNumber: string;
    oldPhone: string;
  }>()
);

export const changeChannelPhone = createAction(
  '[App] change channel Phone',
  props<{
    channel: IChannel;
    phoneNumber: string;
    oldPhone: string;
  }>()
);

export const setChannelOrderId = createAction(
  '[App] set channel order Id',
  props<{
    channel: IChannel;
    orderId: string;
  }>()
);

export const changeChannelOrderId = createAction(
  '[App] change channel order Id',
  props<{
    channel: IChannel;
    orderId: string;
  }>()
);

export const createChannelByParams = createAction(
  '[App] create params channel',
  props<{
    channelByParams: IChannelByParams;
    autoSelect: boolean;
  }>()
);

export const setSmartReplieSelected = createAction('[App] Set Smart Replie Selected', props<{ messages: string | null }>());

export const setCanUseConversationsPolling = createAction(
  '[App] Set can use conversations polling',
  props<{ value: boolean }>()
);

export const setLoadingMessages = createAction(
  '[App] Set loading messages',
  props<{ channelUID: string; value: boolean }>()
);

export const setLoadingOldMessages = createAction(
  '[App] Set loading old messages',
  props<{ channelUID: string; value: boolean }>()
);

export const setMessages = createAction(
  '[App] Set Messages',
  props<{ messages: IMessage[]; concat: boolean; currentChannel: IChannel }>()
);

export const checkNewMessagesByDate = createAction(
  '[App] checkNewMessagesByDate',
  props<{ date: Date; channel: IChannel }>()
);

export const setSmartRepliesEnabled = createAction('[App] Set smart Replies Enabled', props<{ value: boolean }>());

export const getMessages = createAction(
  '[App] Get Messages',
  props<{ channel: IChannel; concat?: boolean; setLoading: boolean }>()
);

export const pinChannel = createAction('[App] Pin Channel', props<{ channel: IChannel; pinned: boolean }>());

export const changeChannelStatus = createAction(
  '[App] change channel status',
  props<{ channel: IChannel; newStatus: ChannelStatus; oldStatus: ChannelStatus }>()
);

export const setChannelStatus = createAction(
  '[App] set channel status',
  props<{ channel: IChannel; value: ChannelStatus }>()
);

export const setChannelMessagesEndDate = createAction(
  '[App] set channel messages end date',
  props<{ channel: IChannel; value: Date }>()
);

export const muteChannel = createAction('[App] mute Channel', props<{ channel: IChannel; mute: boolean }>());

export const unsetChat = createAction('[App] unset Chat');

export const loadDealers = createAction('[App] Load Dealers');

export const unsetDealers = createAction('[App] unset Dealers');

export const selectDealer = createAction('[App] Select dealer', props<{ dealer: IDealer }>());

export const setDealers = createAction('[App] Set Dealers', props<{ dealers: IDealer[] }>());

export const setSearchTerm = createAction('[App] Set search term', props<{ searchTerm: string }>());

export const setChannelsFilter = createAction('[App] Set filter channels', props<{ filter: IChannelFilter }>());

export const setNewChannel = createAction('[App] Set new Channel', props<{ channel: IChannel }>());

export const unsetMessages = createAction('[App] Unset messages');

export const unsetChannel = createAction('[App] Unset channel');

export const setRepairOrder = createAction(
  '[App] set Repair Order',
  props<{ message: IMessage; repairOrder: IRepairOrder; video: string }>()
);

export const setInputPhoneNumber = createAction('[App] set input phone number', props<{ phoneNumber: string | null }>());

export const setFilterValue = createAction(
  '[App] set filters value',
  props<{ value: boolean | string | null; key: string }>()
);

export const setNewMessage = createAction('[App] Set new message', props<{ message: IMessage }>());

export const setVideoCall = createAction('[App] Set Video Call room ', props<{ videoCall: IVideoCallSettings | null }>());

export const setCompletedMessagesChannel = createAction(
  '[App] Set completed messages channel ',
  props<{ channelUID: string; value: boolean }>()
);

export const setReadChannel = createAction(
  '[App] Set read channel by value ',
  props<{ channel: IChannel; value: boolean }>()
);

export const changeReadChannelStatus = createAction(
  '[App] change read channel status ',
  props<{ channel: IChannel; value: boolean }>()
);

export const readAllMessages = createAction('[App] Read all messages');

export const setAllChannelsRead = createAction('[App] Set all channels as read');

export const getChannelOldMessagesValue = createAction(
  '[App] get Channel Old Messages  Value',
  props<{ channel: IChannel; startDate: Date }>()
);

export const getMessagesByTagMessage = createAction(
  '[App] get messages by tag message',
  props<{ channel: IChannel; message: IMessage }>()
);

export const setChannelOldMessagesValue = createAction(
  '[App] set Channel Old Messages  Value',
  props<{ channel: IChannel; value: boolean }>()
);

export const setMessageDeliveryStatus = createAction(
  '[App] Set message delivery status',
  props<{
    messageUID: string;
    status: DeliveryStatusType;
    channelUID: string;
    newMessageUID?: string;
    attachments?: IAttachment[];
    body?: string;
    errorMessage?: string;
  }>()
);

export const changeMessageDeliveryStatus = createAction(
  '[App] Change message delivery status',
  props<{
    messageUID: string;
    status: DeliveryStatusType;
    channelUID: string;
    errorMessage?: string;
  }>()
);

export const resendMessage = createAction('[App] Resend message ', props<{ message: IMessage }>());

export const deleteMessagesById = createAction('[App] delete message by id ', props<{ messageUid: string }>());

export const unsetMessage = createAction('[App] Unset message ', props<{ messageUid: string; channelUid: string }>());

export const deleteChannel = createAction('[App] Delete channel ', props<{ uid: string }>());

export const deleteMemberInChannel = createAction('[App] delete member in channel ', props<{ members: string[] }>());

export const changeMemberCountInChannel = createAction(
  '[App] change member count in channel',
  props<{ channelId: string; value: number }>()
);

export const changeMembersInChannel = createAction(
  '[App] change members in channel',
  props<{ channelId: string; members: IMessageableEntity[] }>()
);

export const addMembersChannel = createAction('[App] Add members channel ', props<{ members: string[] }>());
export const leaveChannel = createAction('[App] Leave channel ', props<{ channelUID: string }>());

export const leaveChannelMember = createAction('[App] Leave channel member ', props<{ channelUID: string }>());

export const deleteSelectedChannel = createAction('[App] Delete selected channel');

export const getTags = createAction('[App] get tags');

export const setTags = createAction('[App] set tags', props<{ tags: ITag[] }>());

export const joinChannel = createAction('[App] Join channel', props<{ channelUID: string }>());

export const getChannelLang = createAction('[App] get channel lang', props<{ channel: IChannel }>());

export const setChannelLang = createAction('[App] set channel lang', props<{ channelUID: string; lang: string }>());

export const getMessageTranslation = createAction(
  '[App] get message translation',
  props<{ message: IMessage; channel: IChannel }>()
);

export const setUnreadChannelCount = createAction(
  '[App] set unread channel count',
  props<{ value: number; channel: IChannel }>()
);

export const setMessageTranslationStatus = createAction(
  '[App] set message translation status',
  props<{ message: IMessage; channel: IChannel; status: boolean; error?: boolean }>()
);

export const setMessageTranslated = createAction(
  '[App] set message translated',
  props<{ message: IMessage; translation: string; channelUID: string }>()
);

export const setChannelTranslationSetting = createAction(
  '[App] set channel translation setting',
  props<{ value: boolean; channelUID: string }>()
);

export const setChannelShowOldMessages = createAction(
  '[App] set channel show old messages',
  props<{ value: boolean; channelUID: string }>()
);

export const setChannelHasOldMessages = createAction(
  '[App] set channel has old messages',
  props<{ value: boolean; channelUID: string }>()
);

export const getSmartReplies = createAction('[App] get smart replies', props<{ channel: IChannel }>());

export const setSmartReplies = createAction(
  '[App] set smart replies',
  props<{ smartReplies: Suggestion[]; channelUID: string }>()
);

export const getStandardReplies = createAction('[App] get Standard replies', props<{ channel: IChannel }>());

export const setStandardReplies = createAction(
  '[App] set Standard replies',
  props<{
    standardReplies: IStandardResponse[];
    channel: IChannel;
    selectedWorkspace: IWorkspace;
    context: IStandardResponseContext;
  }>()
);

export const setChannelMembersPhotos = createAction(
  '[App] set channel members photos',
  props<{ members: IMessageableEntity[]; channelUID: string }>()
);

export const getChannelMembersPhotos = createAction('[App] get channel members photos', props<{ channel: IChannel }>());

export const setCanUseCache = createAction('[App] set can use cache', props<{ value: boolean }>());

export const getMessagesTemplates = createAction('[App] get messages templates', props<{ channel: IChannel }>());

export const setMessagesTemplates = createAction('[App] set messages templates', props<{ templates: IMessageTemplate[] }>());

export const getUsers = createAction('[App] get users', props<{ uid?: string; size?: number; page?: number }>());

export const setLoadingSmartReplies = createAction(
  '[APP] set loading Smart Replies',
  props<{ value: boolean; channelUID: string }>()
);
export const setLoadingStandardReplies = createAction(
  '[APP] set loading standard Replies',
  props<{ value: boolean; channelUID: string }>()
);

export const setChannelStatusEnabled = createAction(
  '[APP] set channel status enabled',
  props<{ value: boolean; channelUID: string }>()
);

export const setChannelConsent = createAction(
  '[APP] set channel consent',
  props<{ value: ChannelConsent; channelUID: string }>()
);
export const setChannelExpirationDate = createAction(
  '[APP] set channel expiration date',
  props<{ expirationDate: Date; channelUID: string }>()
);
export const getUnreadMessages = createAction('[APP] get unread Messages');

export const setUnreadMessages = createAction('[APP] set unread Messages', props<{ count: number }>());

export const setUsers = createAction('[APP] set users', props<{ users: IMessageableEntity[] }>());

export const updateUserOnUsers = createAction(
  '[APP] set user on users',
  props<{ displayName: string; id: string; profileImageURL: string }>()
);

export const setEndMessagesOnChannel = createAction(
  '[APP] set end messages on channel',
  props<{ channel: IChannel; value: Date }>()
);

export const setStartMessagesOnChannel = createAction(
  '[APP] set start messages on channel',
  props<{ channel: IChannel; value: Date | null }>()
);

export const setMessageReaction = createAction(
  '[APP] Set message reaction',
  props<{ channelUID: string; messageUID: string; reaction: IReaction }>()
);

export const applyMessageReaction = createAction(
  '[APP] Apply remote message reaction',
  props<{ channelUID: string; messageUID: string; reaction: IReaction }>()
);

export const setCompleteChatState = createAction('[APP] Set complete chat state', props<{ savedState: IChatState }>());
